import { Down, ListNumbers, Radar, Up } from '@icon-park/react'
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Button, Icon } from '@/components'
import { TagOption } from '@/components/core'
import { WideForm } from '@/components/core/WideForm/WideForm.component'
import { ManualCard } from '@/components/derived'
import { FullScreenModal } from '@/components/derived/FullScreenModal/FullScreenModal.component'
import { useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { Dialog } from '@/modules/dialog'
import { DialogContext } from '@/modules/dialog/Dialog.context'
import { ClassificationTestStage } from '@shared/models'
import { useCompanyContext } from '../CompanyDetails.context'
import { ClassificationTest } from './components/ClassificationTest/ClassificationTest.component'
import { DataSourceExclusion } from './components/DataSourceExclusion/DataSourceExclusion.component'
import { GlobalContextRules } from './components/GlobalContextRules.component'
import { usePromptRulesData } from './hooks/usePromptRulesData.hooks'
import { createPromptRulesPayload } from './services/promptRulesService'
import {
  BrandFocus,
  BrandRules,
  BrandTerms,
  ClassificationTestItem,
  DataSourceType,
  ExpandedRowsState,
  FocusState,
  RowType,
  RulesState,
  TabType,
  TermsState,
} from './types'
import { CONSTANTS, getRowHeight, hasValidSpecificRulesForPolarity, parseRules } from './utils/index'

import { HealthStatus } from '@/pages/Home/Home.component'
import {
  ExpandButton,
  LoadingContainer,
  Polarity,
  PolarityRow,
  PolarityRowsContainer,
  PromptFormContent,
  SaveButton,
  SaveButtonContainer,
  SubtitleContainer,
} from './Prompt.styles'
import { FocusSection } from './components/FocusSection/FocusSection.component'
import { GlobalPriorityRules } from './components/GlobalPriorityRules.component'
import { NeutralPolarityForm, PolarityForm } from './components/PolarityForm/PolarityForm.component'
import { CompoundPolarityRenderer } from './components/PolarityRenderer/PolarityRenderer.component'
import { usePreviewDialogs } from './components/PreviewDialogs/PreviewDialogs.component'
import { PromptHeader } from './components/PromptHeader/PromptHeader.component'
import { SummaryPreview } from './components/SummaryPreview/SummaryPreview.component'

export const Prompt = memo(Component)

function Component(): JSX.Element | null {
  const { companyID, changeQueryParam } = useRouting()
  const { company } = useCompanyContext()

  const [, setCurrentRules] = useState<BrandRules>({
    social: {
      positiveGeneral: '',
      positiveSpecific: '',
      negativeGeneral: '',
      negativeSpecific: '',
      favorableGeneral: '',
      favorableSpecific: '',
      unfavorableGeneral: '',
      unfavorableSpecific: '',
      neutralRules: '',
    },
    press: {
      positiveGeneral: '',
      positiveSpecific: '',
      negativeGeneral: '',
      negativeSpecific: '',
      favorableGeneral: '',
      favorableSpecific: '',
      unfavorableGeneral: '',
      unfavorableSpecific: '',
      neutralRules: '',
    },
  })

  const isConfigured = Boolean(company?.healthCheck?.isPromptChecked)
  const hasSomeRule = Boolean(company?.counts?.prompts && company.counts.prompts > 0)

  const promptStatus: HealthStatus =
    isConfigured ? 'success'
    : hasSomeRule ? 'warning'
    : 'error'

  const statusDescription =
    isConfigured ? 'Preenchido'
    : hasSomeRule ? 'Incompleto'
    : 'Não preenchido'

  if (!companyID || !company) return null

  return (
    <Dialog.Provider>
      <Dialog.Component />
      <PromptContent onRulesChange={setCurrentRules} />
      <ManualCard
        icon={ListNumbers}
        healthCheck={promptStatus}
        title="Configuração de Prompt"
        tooltip="Personalize as instruções (prompts) que a inteligência artificial usa para gerar as análises nos relatórios. Refine os comandos para ajustar o tom, o estilo e o foco dos textos automáticos."
        description={statusDescription}
        linkName="Editar"
        linkAction={() =>
          changeQueryParam({
            param: 'modal',
            value: 'prompt',
            replaceAll: true,
          })
        }
      />
    </Dialog.Provider>
  )
}

function PromptContent({ onRulesChange }: { onRulesChange?: (rules: BrandRules) => void }): JSX.Element | null {
  const { company, reloadCompany, allBrands, reloadAllBrands } = useCompanyContext()
  const { companyID } = useRouting()
  const { sendAlert } = Alert.Hook()

  const [activeBrand, setActiveBrand] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('social')
  const [expandedRows, setExpandedRows] = useState<ExpandedRowsState>({
    positive: true,
    negative: false,
    favorable: false,
    unfavorable: false,
    neutral: false,
    positiveFavorable: false,
    negativeUnfavorable: false,
  })

  const [testItems, setTestItems] = useState<ClassificationTestItem[]>([])
  const [isRunningTest, setIsRunningTest] = useState(false)
  const [excludedDataSources, setExcludedDataSources] = useState<number[]>([])
  const [loadedBrands, setLoadedBrands] = useState<Record<string, boolean>>({})
  const initialBrandSnapshotsRef = useRef<Record<string, string>>({})
  const initialGlobalSnapshotRef = useRef<string | null>(null)
  const initialExcludedDataSourcesSnapshotRef = useRef<string | null>(null)

  useEffect(() => {
    if (allBrands.length > 0 && !activeBrand) {
      const mainBrand = allBrands.find((brand) => Boolean(brand.mainBrand))
      setActiveBrand(mainBrand?.id || allBrands[0].id)
    }
  }, [allBrands, activeBrand])

  useEffect(() => {
    if (companyID && !allBrands.length) {
      reloadAllBrands()
    }
  }, [companyID, allBrands.length, reloadAllBrands])

  const [loadClassificationTests, , classificationTestsData] = Api.LazyQuery<ClassificationTestItem[]>(
    `companies/${companyID}/classification-tests`,
  )

  useEffect(() => {
    if (companyID) {
      loadClassificationTests()
    }
  }, [companyID, loadClassificationTests])

  useEffect(() => {
    if (classificationTestsData) {
      setTestItems(classificationTestsData)
    }
  }, [classificationTestsData])

  const { run: createClassificationTest } = Api.Command<ClassificationTestItem>(
    Api.Methods.POST,
    `companies/${companyID}/classification-tests`,
  )

  const createTagValidator = useCallback(() => {
    return (newTags: TagOption[], existingTags: TagOption[]): TagOption[] => {
      const existingTexts = existingTags.map((tag) => tag.text.toLowerCase().trim())

      return newTags
        .filter((tag) => {
          const normalizedText = tag.text.toLowerCase().trim()
          return normalizedText.length > 0 && !existingTexts.includes(normalizedText)
        })
        .filter(
          (tag, index, arr) =>
            arr.findIndex((t) => t.text.toLowerCase().trim() === tag.text.toLowerCase().trim()) === index,
        )
    }
  }, [])

  const [focusState, setFocusState] = useState<FocusState>({})
  const [termsState, setTermsState] = useState<TermsState>({})
  const [rules, setRules] = useState<RulesState>({
    global: {
      contextRules: '',
      priorityFirstStage: '',
      prioritySecondStage: '',
    },
    brands: {},
  })

  const currentBrandFocus = activeBrand ? focusState[activeBrand] : null
  const currentBrandTerms = activeBrand ? termsState[activeBrand] : null
  const currentBrandRules = activeBrand ? rules.brands[activeBrand] : null

  const getBrandSnapshot = useCallback(
    (brandID: string): string | null => {
      const brandRules = rules.brands[brandID]
      const brandTerms = termsState[brandID]
      const brandFocus = focusState[brandID]

      if (!brandRules || !brandTerms || !brandFocus) {
        return null
      }

      return JSON.stringify({
        rules: brandRules,
        terms: brandTerms,
        focus: brandFocus,
      })
    },
    [focusState, rules.brands, termsState],
  )

  const getGlobalSnapshot = useCallback(() => JSON.stringify(rules.global), [rules.global])

  const getExcludedDataSourcesSnapshot = useCallback(
    () => JSON.stringify([...excludedDataSources].sort((a, b) => a - b)),
    [excludedDataSources],
  )

  const isBrandContentEmpty = useCallback(
    (brandID: string): boolean => {
      const brandRules = rules.brands[brandID]
      const brandTerms = termsState[brandID]
      const brandFocus = focusState[brandID]

      const hasAnyRuleText =
        brandRules ?
          [...Object.values(brandRules.social), ...Object.values(brandRules.press)].some((value) =>
            Boolean(value?.trim()),
          )
        : false

      const hasAnyTermTag =
        brandTerms ? Object.values(brandTerms).some((tags) => Array.isArray(tags) && tags.length > 0) : false

      const hasAnyFocusTag =
        brandFocus ? Object.values(brandFocus).some((tags) => Array.isArray(tags) && tags.length > 0) : false

      return !hasAnyRuleText && !hasAnyTermTag && !hasAnyFocusTag
    },
    [rules.brands, termsState, focusState],
  )

  const createEmptyBrandData = (): BrandRules => ({
    social: {
      positiveGeneral: '',
      positiveSpecific: '',
      negativeGeneral: '',
      negativeSpecific: '',
      favorableGeneral: '',
      favorableSpecific: '',
      unfavorableGeneral: '',
      unfavorableSpecific: '',
      neutralRules: '',
    },
    press: {
      positiveGeneral: '',
      positiveSpecific: '',
      negativeGeneral: '',
      negativeSpecific: '',
      favorableGeneral: '',
      favorableSpecific: '',
      unfavorableGeneral: '',
      unfavorableSpecific: '',
      neutralRules: '',
    },
  })

  const createEmptyFocusData = (): BrandFocus => ({
    socialClientFocusTags: [],
    socialCompetitorFocusTags: [],
    pressClientFocusTags: [],
    pressCompetitorFocusTags: [],
  })

  const createEmptyTermsData = (): BrandTerms => ({
    socialPositivePublishersTags: [],
    socialNegativePublishersTags: [],
    socialFavorablePublishersTags: [],
    socialUnfavorablePublishersTags: [],
    socialPositiveProprietaryTags: [],
    socialNegativeProprietaryTags: [],
    socialFavorableProprietaryTags: [],
    socialUnfavorableProprietaryTags: [],
    pressPositiveChannelsTags: [],
    pressNegativeChannelsTags: [],
    pressFavorableChannelsTags: [],
    pressUnfavorableChannelsTags: [],
    pressPositiveProprietaryTags: [],
    pressNegativeProprietaryTags: [],
    pressFavorableProprietaryTags: [],
    pressUnfavorableProprietaryTags: [],
    socialPositiveFavorablePublishersTags: [],
    socialPositiveFavorableProprietaryTags: [],
    socialNegativeUnfavorablePublishersTags: [],
    socialNegativeUnfavorableProprietaryTags: [],
    pressPositiveFavorableChannelsTags: [],
    pressPositiveFavorableProprietaryTags: [],
    pressNegativeUnfavorableChannelsTags: [],
    pressNegativeUnfavorableProprietaryTags: [],
  })

  useEffect(() => {
    if (activeBrand && !rules.brands[activeBrand]) {
      setRules((prev) => ({
        ...prev,
        brands: {
          ...prev.brands,
          [activeBrand]: createEmptyBrandData(),
        },
      }))
    }
    if (activeBrand && !focusState[activeBrand]) {
      setFocusState((prev) => ({
        ...prev,
        [activeBrand]: createEmptyFocusData(),
      }))
    }
    if (activeBrand && !termsState[activeBrand]) {
      setTermsState((prev) => ({
        ...prev,
        [activeBrand]: createEmptyTermsData(),
      }))
    }
  }, [activeBrand, rules, focusState, termsState])

  const { isRunning: isSavingRules, run: savePromptRules } = Api.Command<void>(Api.Methods.POST, 'prompt-rules')

  const handleTermsChange = useCallback((brandId: string, updates: Partial<TermsState[string]>) => {
    setTermsState((prev) => ({
      ...prev,
      [brandId]: {
        ...prev[brandId],
        ...updates,
      },
    }))
  }, [])

  const handleFocusChange = useCallback((brandId: string, updates: Partial<FocusState[string]>) => {
    setFocusState((prev) => ({
      ...prev,
      [brandId]: {
        ...prev[brandId],
        ...updates,
      },
    }))
  }, [])

  const handleTabChange = useCallback(
    (tab: TabType) => {
      if (allBrands?.some((brand) => brand.id === tab)) {
        setActiveBrand(tab)
        setActiveTab('social')
      } else {
        setActiveTab(tab)
      }
    },
    [allBrands],
  )

  const handleBrandLoaded = useCallback((brandId: string) => {
    setLoadedBrands((prev) => ({
      ...prev,
      [brandId]: true,
    }))
  }, [])

  const { isLoading } = usePromptRulesData(
    companyID,
    (allBrands || []).map((brand) => brand.id),
    activeBrand,
    Boolean(activeBrand && !loadedBrands[activeBrand]),
    handleBrandLoaded,
    handleFocusChange,
    handleTermsChange,
    setRules,
  )

  useEffect(() => {
    initialBrandSnapshotsRef.current = {}
    initialGlobalSnapshotRef.current = null
    initialExcludedDataSourcesSnapshotRef.current = null
  }, [companyID])

  useEffect(() => {
    const loadedBrandIDs = Object.keys(loadedBrands).filter((brandID) => loadedBrands[brandID])

    for (const brandID of loadedBrandIDs) {
      if (!initialBrandSnapshotsRef.current[brandID]) {
        const snapshot = getBrandSnapshot(brandID)
        if (snapshot) {
          initialBrandSnapshotsRef.current[brandID] = snapshot
        }
      }
    }

    if (initialGlobalSnapshotRef.current === null && loadedBrandIDs.length > 0) {
      initialGlobalSnapshotRef.current = getGlobalSnapshot()
    }

    if (initialExcludedDataSourcesSnapshotRef.current === null && loadedBrandIDs.length > 0) {
      initialExcludedDataSourcesSnapshotRef.current = getExcludedDataSourcesSnapshot()
    }
  }, [getBrandSnapshot, getExcludedDataSourcesSnapshot, getGlobalSnapshot, loadedBrands])

  const brandStatusByDataSource = useMemo(() => {
    const statusByBrand: Record<
      string,
      {
        social: { focus: boolean; premises: boolean }
        press: { focus: boolean; premises: boolean }
      }
    > = {}

    for (const brand of allBrands || []) {
      const brandRules = rules.brands[brand.id]
      const brandFocus = focusState[brand.id]

      if (!brandRules) {
        statusByBrand[brand.id] = {
          social: { focus: false, premises: false },
          press: { focus: false, premises: false },
        }
        continue
      }

      const isSocialExcluded = excludedDataSources.includes(CONSTANTS.SOCIAL_MEDIA_DS_ID)
      const isPressExcluded = excludedDataSources.includes(CONSTANTS.PRESS_DS_ID)

      const socialPremisesComplete =
        isSocialExcluded ||
        [
          hasValidSpecificRulesForPolarity(brandRules, 'social', 'positiveSpecific'),
          hasValidSpecificRulesForPolarity(brandRules, 'social', 'negativeSpecific'),
          hasValidSpecificRulesForPolarity(brandRules, 'social', 'favorableSpecific'),
          hasValidSpecificRulesForPolarity(brandRules, 'social', 'unfavorableSpecific'),
          hasValidSpecificRulesForPolarity(brandRules, 'social', 'neutralRules'),
        ].every(Boolean)

      const pressPremisesComplete =
        isPressExcluded ||
        [
          hasValidSpecificRulesForPolarity(brandRules, 'press', 'positiveSpecific'),
          hasValidSpecificRulesForPolarity(brandRules, 'press', 'negativeSpecific'),
          hasValidSpecificRulesForPolarity(brandRules, 'press', 'favorableSpecific'),
          hasValidSpecificRulesForPolarity(brandRules, 'press', 'unfavorableSpecific'),
          hasValidSpecificRulesForPolarity(brandRules, 'press', 'neutralRules'),
        ].every(Boolean)

      const socialFocusComplete = isSocialExcluded || Boolean(brandFocus?.socialClientFocusTags?.length)

      const pressFocusComplete = isPressExcluded || Boolean(brandFocus?.pressClientFocusTags?.length)

      statusByBrand[brand.id] = {
        social: {
          focus: socialFocusComplete,
          premises: socialPremisesComplete,
        },
        press: {
          focus: pressFocusComplete,
          premises: pressPremisesComplete,
        },
      }
    }

    return statusByBrand
  }, [allBrands, rules.brands, focusState, excludedDataSources])

  const brandCompletion = useMemo<Record<string, boolean>>(() => {
    const statusByBrand: Record<string, boolean> = {}

    for (const brand of allBrands || []) {
      const current = brandStatusByDataSource[brand.id]
      statusByBrand[brand.id] =
        Boolean(current?.social.focus) &&
        Boolean(current?.social.premises) &&
        Boolean(current?.press.focus) &&
        Boolean(current?.press.premises)
    }

    return statusByBrand
  }, [allBrands, brandStatusByDataSource])

  const dataSourceCompletion = useMemo(() => {
    if (!activeBrand) {
      return {
        social: false,
        press: false,
      }
    }

    const current = brandStatusByDataSource[activeBrand]

    return {
      social: Boolean(current?.social.focus) && Boolean(current?.social.premises),
      press: Boolean(current?.press.focus) && Boolean(current?.press.premises),
    }
  }, [activeBrand, brandStatusByDataSource])

  useEffect(() => {
    if (company?.excludedFavorabilityDataSources) {
      setExcludedDataSources(company.excludedFavorabilityDataSources)
    }
  }, [company?.excludedFavorabilityDataSources])

  useEffect(() => {
    const isSocialExcluded = excludedDataSources.includes(CONSTANTS.SOCIAL_MEDIA_DS_ID)
    const isPressExcluded = excludedDataSources.includes(CONSTANTS.PRESS_DS_ID)

    if (activeTab === 'social' && isSocialExcluded) {
      if (!isPressExcluded) {
        setActiveTab('press')
      }
    } else if (activeTab === 'press' && isPressExcluded) {
      if (!isSocialExcluded) {
        setActiveTab('social')
      }
    }
  }, [excludedDataSources, activeTab])

  const { getDialogForPolarity } = usePreviewDialogs({
    rules: currentBrandRules || createEmptyBrandData(),
    termsState: currentBrandTerms || createEmptyTermsData(),
    activeTab: activeTab === 'press' ? 'press' : 'social',
  })

  function toggleRow(rowType: RowType): void {
    setExpandedRows((prev) => ({
      ...prev,
      [rowType]: !prev[rowType],
    }))
  }

  useEffect(() => {
    if (currentBrandRules) {
      onRulesChange?.(currentBrandRules)
    }
  }, [currentBrandRules, onRulesChange])

  function handleRulesChange(field: string, value: string): void {
    if (activeTab === 'test' || !activeBrand) return

    const isDataSourceTab = activeTab === 'social' || activeTab === 'press'
    if (!isDataSourceTab) return

    if (field === 'global.contextRules') {
      setRules((prev) => ({
        ...prev,
        global: {
          ...prev.global,
          contextRules: value,
        },
      }))
    } else if (field === 'global.priorityFirstStage') {
      setRules((prev) => ({
        ...prev,
        global: {
          ...prev.global,
          priorityFirstStage: value,
        },
      }))
    } else if (field === 'global.prioritySecondStage') {
      setRules((prev) => ({
        ...prev,
        global: {
          ...prev.global,
          prioritySecondStage: value,
        },
      }))
    } else {
      setRules((prev) => ({
        ...prev,
        brands: {
          ...prev.brands,
          [activeBrand]: {
            ...prev.brands[activeBrand],
            [activeTab]: {
              ...prev.brands[activeBrand]?.[activeTab],
              [field]: value,
            },
          },
        },
      }))
    }
  }

  async function onSubmit(): Promise<void> {
    if (!companyID) return

    const availableBrandIDs = (allBrands || [])
      .map((brand) => brand.id)
      .filter((brandID) => loadedBrands[brandID] && Boolean(getBrandSnapshot(brandID)))

    if (!availableBrandIDs.length) {
      sendAlert({
        type: 'error',
        title: 'Nada para salvar',
        message: 'Nenhuma marca carregada para salvar.',
      })
      return
    }

    const currentGlobalSnapshot = getGlobalSnapshot()
    const currentExcludedDataSourcesSnapshot = getExcludedDataSourcesSnapshot()

    const hasGlobalChanges =
      initialGlobalSnapshotRef.current === null || initialGlobalSnapshotRef.current !== currentGlobalSnapshot

    const hasExcludedDataSourcesChanges =
      initialExcludedDataSourcesSnapshotRef.current === null ||
      initialExcludedDataSourcesSnapshotRef.current !== currentExcludedDataSourcesSnapshot

    const skippedBrandIDs: string[] = []

    const changedBrandIDs = availableBrandIDs.filter((brandID) => {
      const currentBrandSnapshot = getBrandSnapshot(brandID)
      if (!currentBrandSnapshot) {
        return false
      }

      const initialBrandSnapshot = initialBrandSnapshotsRef.current[brandID]
      const hasBrandSpecificChanges = !initialBrandSnapshot || initialBrandSnapshot !== currentBrandSnapshot

      if (hasBrandSpecificChanges) {
        return true
      }

      const isPureGlobalChange = hasGlobalChanges || hasExcludedDataSourcesChanges
      if (!isPureGlobalChange) {
        return false
      }

      // Marca não editada nesta sessão, só carregada em segundo plano pro badge de
      // conclusão. Se o snapshot dela está totalmente vazio, não dá pra confiar que é
      // o estado real salvo no banco (pode ser um carregamento que falhou/ficou parcial) -
      // resalvar isso zeraria os dados reais da marca. Ver histórico do incidente de
      // perda de dados de prompt (jul/2026).
      if (brandID !== activeBrand && isBrandContentEmpty(brandID)) {
        skippedBrandIDs.push(brandID)
        return false
      }

      return true
    })

    if (skippedBrandIDs.length) {
      const skippedNames = skippedBrandIDs
        .map((id) => allBrands?.find((brand) => brand.id === id)?.name || id)
        .join(', ')

      sendAlert({
        type: 'error',
        title: 'Atenção: marcas não sincronizadas',
        message: `Não sincronizamos as mudanças globais para: ${skippedNames}. O cache local dessas marcas está vazio, o que pode indicar que ainda não carregou de verdade. Abra a aba de cada uma e salve manualmente para garantir a sincronização.`,
      })
    }

    if (!changedBrandIDs.length) {
      sendAlert({
        type: 'success',
        title: 'Sem alterações',
        message: 'Nenhuma alteração detectada para salvar.',
      })
      return
    }

    const saveByBrand = (brandID: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const payload = createPromptRulesPayload(companyID, brandID, rules, termsState, focusState, excludedDataSources)

        savePromptRules({
          input: payload,
          onSuccess: () => resolve(),
          onFail: (error) => reject(error),
        })
      })

    const savedBrandIDs: string[] = []
    let failedBrandID: string | null = null

    for (const brandID of changedBrandIDs) {
      try {
        await saveByBrand(brandID)
        savedBrandIDs.push(brandID)
      } catch {
        failedBrandID = brandID
        break
      }
    }

    // Confirma o snapshot só das marcas que o backend aceitou. Sem isso, uma falha no meio
    // do lote faria o próximo save reenviar até o que já tinha ido.
    for (const brandID of savedBrandIDs) {
      const snapshot = getBrandSnapshot(brandID)
      if (snapshot) {
        initialBrandSnapshotsRef.current[brandID] = snapshot
      }
    }

    if (failedBrandID) {
      const brandName = (brandID: string): string => allBrands?.find((brand) => brand.id === brandID)?.name || brandID

      const notSentCount = changedBrandIDs.length - savedBrandIDs.length - 1

      // A mensagem do backend já foi exibida pelo interceptor do axios; aqui só dizemos
      // onde o lote parou, pra não empilhar um alerta genérico por cima do específico.
      sendAlert({
        type: 'error',
        title: 'Salvamento interrompido',
        message: [
          `Falha ao salvar a marca ${brandName(failedBrandID)}.`,
          savedBrandIDs.length ? `Já salvas antes disso: ${savedBrandIDs.map(brandName).join(', ')}.` : null,
          notSentCount > 0 ? `${notSentCount} marca(s) não chegaram a ser enviadas.` : null,
        ]
          .filter(Boolean)
          .join(' '),
      })
      return
    }

    initialGlobalSnapshotRef.current = currentGlobalSnapshot
    initialExcludedDataSourcesSnapshotRef.current = currentExcludedDataSourcesSnapshot

    sendAlert({
      type: 'success',
      title: 'Sucesso',
      message: `Premissas de prompt salvas com sucesso para ${changedBrandIDs.length} marca(s)!`,
    })
    reloadCompany()
  }

  function getSaveValidationMessage(): string | null {
    if (!currentBrandRules || !activeBrand) {
      return 'Selecione uma marca para salvar'
    }

    const currentBrandFocusState = focusState[activeBrand]

    const isSocialExcluded = excludedDataSources.includes(CONSTANTS.SOCIAL_MEDIA_DS_ID)
    const isPressExcluded = excludedDataSources.includes(CONSTANTS.PRESS_DS_ID)

    if (isSocialExcluded && isPressExcluded) {
      return 'Pelo menos uma fonte de dados deve estar ativa'
    }

    const socialComplete =
      isSocialExcluded ? true : (
        [
          hasValidSpecificRulesForPolarity(currentBrandRules, 'social', 'positiveSpecific'),
          hasValidSpecificRulesForPolarity(currentBrandRules, 'social', 'negativeSpecific'),
          hasValidSpecificRulesForPolarity(currentBrandRules, 'social', 'favorableSpecific'),
          hasValidSpecificRulesForPolarity(currentBrandRules, 'social', 'unfavorableSpecific'),
          hasValidSpecificRulesForPolarity(currentBrandRules, 'social', 'neutralRules'),
        ].every(Boolean)
      )

    const pressComplete =
      isPressExcluded ? true : (
        [
          hasValidSpecificRulesForPolarity(currentBrandRules, 'press', 'positiveSpecific'),
          hasValidSpecificRulesForPolarity(currentBrandRules, 'press', 'negativeSpecific'),
          hasValidSpecificRulesForPolarity(currentBrandRules, 'press', 'favorableSpecific'),
          hasValidSpecificRulesForPolarity(currentBrandRules, 'press', 'unfavorableSpecific'),
          hasValidSpecificRulesForPolarity(currentBrandRules, 'press', 'neutralRules'),
        ].every(Boolean)
      )

    const socialRulesValid = [
      'positiveSpecific',
      'negativeSpecific',
      'favorableSpecific',
      'unfavorableSpecific',
      'neutralRules',
    ].every((field) => {
      if (isSocialExcluded) return true
      const rulesText = currentBrandRules.social[field as keyof typeof currentBrandRules.social]
      const rulesCount = parseRules(rulesText).length
      return rulesCount <= CONSTANTS.MAX_RULES_PER_POLARITY
    })

    const pressRulesValid = [
      'positiveSpecific',
      'negativeSpecific',
      'favorableSpecific',
      'unfavorableSpecific',
      'neutralRules',
    ].every((field) => {
      if (isPressExcluded) return true
      const rulesText = currentBrandRules.press[field as keyof typeof currentBrandRules.press]
      const rulesCount = parseRules(rulesText).length
      return rulesCount <= CONSTANTS.MAX_RULES_PER_POLARITY
    })

    const focusComplete =
      (isSocialExcluded || Boolean(currentBrandFocusState?.socialClientFocusTags?.length)) &&
      (isPressExcluded || Boolean(currentBrandFocusState?.pressClientFocusTags?.length))

    if (!socialRulesValid || !pressRulesValid) {
      return `Cada campo obrigatório deve ter entre 1 e ${CONSTANTS.MAX_RULES_PER_POLARITY} regras`
    }

    if (!socialComplete || !pressComplete) {
      return 'Preencha as premissas obrigatórias (mínimo de 1 regra por campo obrigatório)'
    }

    if (!focusComplete) {
      return 'Preencha ao menos 1 foco cliente nas fontes ativas'
    }

    return null
  }

  function SummaryDialogActions(): JSX.Element {
    const context = useContext(DialogContext)
    return (
      <>
        <Button.Root
          onClick={() => context?.close()}
          data-variant="secondary"
          disabled={isSavingRules}
          $disabledMessage="Salvando dados, aguarde...">
          <Button.Text value="Cancelar" />
        </Button.Root>
        <Button.Root
          $variant="primary"
          onClick={() => {
            onSubmit()
            context?.close()
          }}
          $loading={isSavingRules}
          disabled={isSavingRules}
          $disabledMessage="Salvando dados, aguarde...">
          <Button.Text value="Estou ciente, salvar agora!" />
        </Button.Root>
      </>
    )
  }

  const summaryDialog = Dialog.Hook({
    title: 'Resumo das Premissas de Prompt',
    content: (
      <SummaryPreview
        brands={allBrands || []}
        rulesByBrand={rules.brands}
        termsByBrand={termsState}
        focusByBrand={focusState}
        brandCompletion={brandCompletion}
        excludedDataSources={excludedDataSources}
      />
    ),
    size: 'large',
    customActions: <SummaryDialogActions />,
  })

  const handleRunClassificationTest = useCallback(
    async (testData: {
      startDate: string
      endDate: string
      dataSources: ('social' | 'press')[]
      stages: ClassificationTestStage[]
    }) => {
      if (!companyID || !activeBrand) return

      setIsRunningTest(true)

      try {
        createClassificationTest({
          input: {
            companyID,
            brandID: activeBrand,
            dataSource: testData.dataSources,
            startDate: testData.startDate,
            endDate: testData.endDate,
            stages: testData.stages,
          },
          onSuccess: (newTest) => {
            sendAlert({
              type: 'success',
              title: 'Teste Iniciado',
              message: 'O teste de classificação foi iniciado com sucesso!',
            })
            setTestItems((prev) => [newTest, ...prev])
            loadClassificationTests()
          },
          onFail: () => {
            sendAlert({
              type: 'error',
              title: 'Erro',
              message: 'Erro ao iniciar o teste de classificação.',
            })
          },
        })
      } catch (error) {
        sendAlert({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao iniciar o teste de classificação.',
        })
      } finally {
        setIsRunningTest(false)
      }
    },
    [companyID, activeBrand, sendAlert, createClassificationTest, loadClassificationTests],
  )

  if (isLoading) {
    return (
      <FullScreenModal
        controlKey="prompt"
        backToName={company?.name}
        title="Configuração de Prompt"
        subtitle={
          <SubtitleContainer>
            <span>Carregando...</span>
          </SubtitleContainer>
        }
        icon={Radar}
        beforeClose={reloadCompany}>
        <LoadingContainer>
          <span>Carregando premissas de prompt...</span>
        </LoadingContainer>
      </FullScreenModal>
    )
  }

  return (
    <FullScreenModal
      controlKey="prompt"
      backToName={company?.name}
      title="Configuração de Prompt"
      subtitle={
        <SubtitleContainer>
          <span>
            Preencha todos os campos <strong>obrigatórios (*)</strong> para concluir o cadastro da configuração de
            prompt.
          </span>
        </SubtitleContainer>
      }
      icon={Radar}
      beforeClose={reloadCompany}>
      <WideForm onSubmit={onSubmit} controlParam={true} fullHeight={false} shouldExpand={false}>
        <PromptFormContent $fullHeight={false} $shouldExpand={false}>
          <GlobalContextRules
            globalRules={rules.global.contextRules}
            onGlobalRulesChange={(value) => handleRulesChange('global.contextRules', value)}
          />

          <GlobalPriorityRules
            firstStageRules={rules.global.priorityFirstStage}
            secondStageRules={rules.global.prioritySecondStage}
            onFirstStageRulesChange={(value) => handleRulesChange('global.priorityFirstStage', value)}
            onSecondStageRulesChange={(value) => handleRulesChange('global.prioritySecondStage', value)}
          />

          <DataSourceExclusion excludedDataSources={excludedDataSources} onExclusionChange={setExcludedDataSources} />

          <PromptHeader
            activeTab={activeTab}
            onTabChange={handleTabChange}
            excludedDataSources={excludedDataSources}
            brands={allBrands || []}
            activeBrand={activeBrand}
            brandCompletion={brandCompletion}
            dataSourceCompletion={dataSourceCompletion}
          />

          {(() => {
            const isSocialExcluded = excludedDataSources.includes(CONSTANTS.SOCIAL_MEDIA_DS_ID)
            const isPressExcluded = excludedDataSources.includes(CONSTANTS.PRESS_DS_ID)
            const bothExcluded = isSocialExcluded && isPressExcluded

            if (bothExcluded) {
              return null
            }

            const isCurrentTabExcluded =
              (activeTab === 'social' && isSocialExcluded) || (activeTab === 'press' && isPressExcluded)

            if (isCurrentTabExcluded) {
              return null
            }

            if (activeTab === 'test') {
              return (
                <ClassificationTest
                  onRunTest={handleRunClassificationTest}
                  activeBrandID={activeBrand}
                  isRunning={isRunningTest}
                  testItems={testItems}
                />
              )
            }

            return (
              <>
                <FocusSection
                  clientFocusTags={
                    activeTab === 'social' ?
                      currentBrandFocus?.socialClientFocusTags || []
                    : currentBrandFocus?.pressClientFocusTags || []
                  }
                  competitorFocusTags={
                    activeTab === 'social' ?
                      currentBrandFocus?.socialCompetitorFocusTags || []
                    : currentBrandFocus?.pressCompetitorFocusTags || []
                  }
                  clientFocusComplete={
                    activeTab === 'social' ?
                      Boolean(currentBrandFocus?.socialClientFocusTags?.length)
                    : Boolean(currentBrandFocus?.pressClientFocusTags?.length)
                  }
                  onClientFocusChange={(tags) => {
                    if (!activeBrand) return
                    handleFocusChange(
                      activeBrand,
                      activeTab === 'social' ? { socialClientFocusTags: tags } : { pressClientFocusTags: tags },
                    )
                  }}
                  onCompetitorFocusChange={(tags) => {
                    if (!activeBrand) return
                    handleFocusChange(
                      activeBrand,
                      activeTab === 'social' ? { socialCompetitorFocusTags: tags } : { pressCompetitorFocusTags: tags },
                    )
                  }}
                  validationConfig={{
                    maxCharacters: 200,
                    allowDuplicates: false,
                    customValidator: createTagValidator(),
                  }}
                />

                <PolarityRowsContainer>
                  <PolarityRow $expanded={expandedRows.positive} $height={getRowHeight(expandedRows, 'positive')}>
                    <Polarity $expanded={expandedRows.positive}>
                      <PolarityForm
                        title="POSITIVO"
                        specificField="positiveSpecific"
                        expanded={expandedRows.positive}
                        polarity="POSITIVO"
                        activeTab={activeTab as DataSourceType}
                        rules={currentBrandRules || createEmptyBrandData()}
                        termsState={currentBrandTerms || createEmptyTermsData()}
                        onRulesChange={handleRulesChange}
                        onTermsChange={(updates) => {
                          if (!activeBrand) return
                          handleTermsChange(activeBrand, updates)
                        }}
                        onPreviewClick={() => getDialogForPolarity('POSITIVO')?.open()}
                        tagValidator={createTagValidator()}
                        required
                        showRequiredLabel={!expandedRows.positive}
                      />
                    </Polarity>
                    <ExpandButton onClick={() => toggleRow('positive')}>
                      <Icon value={expandedRows.positive ? Up : Down} color="#01c38d" size={20} />
                    </ExpandButton>
                  </PolarityRow>

                  <PolarityRow $expanded={expandedRows.negative} $height={getRowHeight(expandedRows, 'negative')}>
                    <Polarity $expanded={expandedRows.negative}>
                      <PolarityForm
                        title="NEGATIVO"
                        specificField="negativeSpecific"
                        expanded={expandedRows.negative}
                        polarity="NEGATIVO"
                        activeTab={activeTab as DataSourceType}
                        rules={currentBrandRules || createEmptyBrandData()}
                        termsState={currentBrandTerms || createEmptyTermsData()}
                        onRulesChange={handleRulesChange}
                        onTermsChange={(updates) => {
                          if (!activeBrand) return
                          handleTermsChange(activeBrand, updates)
                        }}
                        onPreviewClick={() => getDialogForPolarity('NEGATIVO')?.open()}
                        tagValidator={createTagValidator()}
                        required
                        showRequiredLabel={!expandedRows.negative}
                      />
                    </Polarity>
                    <ExpandButton onClick={() => toggleRow('negative')}>
                      <Icon value={expandedRows.negative ? Up : Down} color="#01c38d" size={20} />
                    </ExpandButton>
                  </PolarityRow>

                  <PolarityRow $expanded={expandedRows.favorable} $height={getRowHeight(expandedRows, 'favorable')}>
                    <Polarity $expanded={expandedRows.favorable}>
                      <PolarityForm
                        title="FAVORÁVEL"
                        specificField="favorableSpecific"
                        expanded={expandedRows.favorable}
                        polarity="FAVORAVEL"
                        activeTab={activeTab as DataSourceType}
                        rules={currentBrandRules || createEmptyBrandData()}
                        termsState={currentBrandTerms || createEmptyTermsData()}
                        onRulesChange={handleRulesChange}
                        onTermsChange={(updates) => {
                          if (!activeBrand) return
                          handleTermsChange(activeBrand, updates)
                        }}
                        onPreviewClick={() => getDialogForPolarity('FAVORÁVEL')?.open()}
                        tagValidator={createTagValidator()}
                        required
                        showRequiredLabel={!expandedRows.favorable}
                      />
                    </Polarity>
                    <ExpandButton onClick={() => toggleRow('favorable')}>
                      <Icon value={expandedRows.favorable ? Up : Down} color="#01c38d" size={20} />
                    </ExpandButton>
                  </PolarityRow>

                  <PolarityRow $expanded={expandedRows.unfavorable} $height={getRowHeight(expandedRows, 'unfavorable')}>
                    <Polarity $expanded={expandedRows.unfavorable}>
                      <PolarityForm
                        title="DESFAVORÁVEL"
                        specificField="unfavorableSpecific"
                        expanded={expandedRows.unfavorable}
                        polarity="DESFAVORAVEL"
                        activeTab={activeTab as DataSourceType}
                        rules={currentBrandRules || createEmptyBrandData()}
                        termsState={currentBrandTerms || createEmptyTermsData()}
                        onRulesChange={handleRulesChange}
                        onTermsChange={(updates) => {
                          if (!activeBrand) return
                          handleTermsChange(activeBrand, updates)
                        }}
                        onPreviewClick={() => getDialogForPolarity('DESFAVORÁVEL')?.open()}
                        tagValidator={createTagValidator()}
                        required
                        showRequiredLabel={!expandedRows.unfavorable}
                      />
                    </Polarity>
                    <ExpandButton onClick={() => toggleRow('unfavorable')}>
                      <Icon value={expandedRows.unfavorable ? Up : Down} color="#01c38d" size={20} />
                    </ExpandButton>
                  </PolarityRow>
                  <PolarityRow $expanded={expandedRows.neutral} $height={getRowHeight(expandedRows, 'neutral')}>
                    <Polarity $expanded={expandedRows.neutral}>
                      <NeutralPolarityForm
                        title="NEUTRO"
                        expanded={expandedRows.neutral}
                        activeTab={activeTab as DataSourceType}
                        rules={currentBrandRules || createEmptyBrandData()}
                        onRulesChange={handleRulesChange}
                        required
                        showRequiredLabel={!expandedRows.neutral}
                        onPreviewClick={() => getDialogForPolarity('NEUTRO')?.open()}
                      />
                    </Polarity>
                    <ExpandButton onClick={() => toggleRow('neutral')}>
                      <Icon value={expandedRows.neutral ? Up : Down} color="#01c38d" size={20} />
                    </ExpandButton>
                  </PolarityRow>
                  <PolarityRow
                    $expanded={expandedRows.positiveFavorable}
                    $height={getRowHeight(expandedRows, 'positiveFavorable')}>
                    <Polarity $expanded={expandedRows.positiveFavorable}>
                      <CompoundPolarityRenderer
                        title="POSITIVO / FAVORÁVEL"
                        expanded={expandedRows.positiveFavorable}
                        polarity="POSITIVO_FAVORAVEL"
                        activeTab={activeTab as DataSourceType}
                        termsState={currentBrandTerms || createEmptyTermsData()}
                        onTermsChange={(updates) => {
                          if (!activeBrand) return
                          handleTermsChange(activeBrand, updates)
                        }}
                        onPreviewClick={() => getDialogForPolarity('POSITIVO / FAVORÁVEL')?.open()}
                        tagValidator={createTagValidator()}
                      />
                    </Polarity>
                    <ExpandButton onClick={() => toggleRow('positiveFavorable')}>
                      <Icon value={expandedRows.positiveFavorable ? Up : Down} color="#01c38d" size={20} />
                    </ExpandButton>
                  </PolarityRow>
                  <PolarityRow
                    $expanded={expandedRows.negativeUnfavorable}
                    $height={getRowHeight(expandedRows, 'negativeUnfavorable')}>
                    <Polarity $expanded={expandedRows.negativeUnfavorable}>
                      <CompoundPolarityRenderer
                        title="NEGATIVO / DESFAVORÁVEL"
                        expanded={expandedRows.negativeUnfavorable}
                        polarity="NEGATIVO_DESFAVORAVEL"
                        activeTab={activeTab as DataSourceType}
                        termsState={currentBrandTerms || createEmptyTermsData()}
                        onTermsChange={(updates) => {
                          if (!activeBrand) return
                          handleTermsChange(activeBrand, updates)
                        }}
                        onPreviewClick={() => getDialogForPolarity('NEGATIVO / DESFAVORÁVEL')?.open()}
                        tagValidator={createTagValidator()}
                      />
                    </Polarity>
                    <ExpandButton onClick={() => toggleRow('negativeUnfavorable')}>
                      <Icon value={expandedRows.negativeUnfavorable ? Up : Down} color="#01c38d" size={20} />
                    </ExpandButton>
                  </PolarityRow>
                </PolarityRowsContainer>
              </>
            )
          })()}
        </PromptFormContent>

        {(() => {
          const isSocialExcluded = excludedDataSources.includes(CONSTANTS.SOCIAL_MEDIA_DS_ID)
          const isPressExcluded = excludedDataSources.includes(CONSTANTS.PRESS_DS_ID)
          const bothExcluded = isSocialExcluded && isPressExcluded

          if (activeTab === 'test' || bothExcluded) {
            return null
          }

          return (
            <SaveButtonContainer>
              <SaveButton>
                {(() => {
                  const saveValidationMessage = getSaveValidationMessage()
                  return (
                    <Button.Root
                      $variant="primary"
                      onClick={summaryDialog.open}
                      disabled={Boolean(saveValidationMessage) || isSavingRules}
                      $disabledMessage={
                        isSavingRules ? 'Salvando dados, aguarde...' : (
                          saveValidationMessage || 'Complete todas as regras para salvar'
                        )
                      }>
                      <Button.Text value="Visualizar e Salvar" />
                    </Button.Root>
                  )
                })()}
              </SaveButton>
            </SaveButtonContainer>
          )
        })()}
      </WideForm>
    </FullScreenModal>
  )
}
